import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Modal, Form, Input, Select, message, Tag, Popconfirm, InputNumber, Typography, Divider, Row, Col, Tooltip, Collapse, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined, InfoCircleOutlined, SettingOutlined, HddOutlined, DesktopOutlined, ApiOutlined } from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';
import I18nFieldTabs, { parseI18n, cleanI18n } from '../../components/I18nFieldTabs';
import { currencySymbol, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import { useTranslation } from '../../../shared/i18n';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

// Overage config (EvmQuotaService): defaults prefilled for new plans.
const QUOTA_DEFAULTS = {
    cpu_quota_core_hours: 0, io_quota_gb: 0, io_read_quota_gb: 0, io_write_quota_gb: 0,
    cpu_over_action: 'throttle', io_over_action: 'throttle',
    throttle_net_mbps: 1, throttle_cpu_pct: 10,
    throttle_io_read_mbps: 20, throttle_io_write_mbps: 10, throttle_io_read_iops: 500, throttle_io_write_iops: 300,
};
const OVER_ACTIONS = [
    { value: 'throttle', label: '限速' },
    { value: 'cut_public', label: '断公网' },
    { value: 'shutdown', label: '关机' },
];

const TemplateManagement = () => {
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [images, setImages] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const [modalVisible, setModalVisible] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [templateType, setTemplateType] = useState('static');
    const [filters, setFilters] = useState({ keyword: '', type: '' });
    const [deviceOptions, setDeviceOptions] = useState(null);
    const [showDeviceConfig, setShowDeviceConfig] = useState(false);
    const [form] = Form.useForm();
    const { t } = useTranslation();
    // 'charge' (per GB, EvmTrafficCharge) exists for traffic only
    const TRAFFIC_OVER_ACTIONS = [...OVER_ACTIONS, {
        value: 'charge',
        label: <Tooltip title={t('Bill traffic above the quota per GB at the zone price; throttle at the cap')}>{t('Charge per GB')}</Tooltip>,
    }];

    useEffect(() => {
        fetchTemplates();
        fetchImages();
        fetchDeviceOptions();
    }, [pagination.current, filters]);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const response = await evmAdminService.getTemplateList({
                page: pagination.current,
                per_page: pagination.pageSize,
                ...filters,
            });
            if (response.status === 'success') {
                setTemplates(response.data?.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data?.total || 0,
                }));
            }
        } catch (error) {
            message.error('Failed to fetch templates');
        }
        setLoading(false);
    };

    const fetchImages = async () => {
        try {
            const response = await evmAdminService.getImageList({ per_page: 100, status: 'enable' });
            if (response.status === 'success') {
                setImages(response.data?.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch images');
        }
    };

    const fetchDeviceOptions = async () => {
        try {
            const response = await evmAdminService.getTemplateDeviceOptions();
            if (response.status === 'success') {
                setDeviceOptions(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch device options');
        }
    };

    const handleCreate = (type = 'static') => {
        setEditingTemplate(null);
        setTemplateType(type);
        setShowDeviceConfig(false);
        form.resetFields();
        const defaults = deviceOptions?.defaults || {};
        form.setFieldsValue({
            type: type,
            core: type === 'elastic' ? '1-8-1' : 1,
            memory: type === 'elastic' ? '512-8192-512' : 1024,
            sys_disk_size: type === 'elastic' ? '20-100-10' : 20,
            data_disk_size: type === 'elastic' ? '0-500-10' : 0,
            net_in: type === 'elastic' ? '1-100-1' : 10,
            net_out: type === 'elastic' ? '1-100-1' : 10,
            quota: 100,
            cpu_time: 900,
            over_cpu: 5,
            snapshot_num: 3,
            backup_num: 1,
            backup_points: 0,
            backup_interval_hours: 24,
            public_ip_num: 1,
            private_ip_num: 0,
            nat_ip_num: 0,
            network_type: 'public',
            port_forward_num: 20,
            site_forward_num: 10,
            ipv6_prefix_len: 0,
            ipv6_count: 1,
            flow_limit: 0,
            ...QUOTA_DEFAULTS,
            traffic_over_action: 'throttle',
            status: 1,
            // Device config defaults
            sys_disk_driver: defaults.sys_disk_driver || 'virtio',
            sys_disk_cache: defaults.sys_disk_cache || 'none',
            sys_disk_io: defaults.sys_disk_io || 'native',
            data_disk_driver: defaults.data_disk_driver || 'virtio',
            data_disk_cache: defaults.data_disk_cache || 'none',
            data_disk_io: defaults.data_disk_io || 'native',
            net_driver: defaults.net_driver || 'virtio',
            display_type: defaults.display_type || 'vnc',
            video_model: defaults.video_model || 'cirrus',
            vnc_keymap: defaults.vnc_keymap || 'en-us',
            cpu_mode: defaults.cpu_mode || 'host-passthrough',
            machine_type: defaults.machine_type || 'pc-q35-6.2',
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingTemplate(record);
        setTemplateType(record.type || 'static');
        // Check if record has device config values
        const hasDeviceConfig = record.sys_disk_driver || record.net_driver || record.display_type;
        setShowDeviceConfig(hasDeviceConfig);
        // Parse price JSON if exists
        let price = {};
        try {
            price = record.price ? JSON.parse(record.price) : {};
        } catch (e) {
            price = {};
        }
        const defaults = deviceOptions?.defaults || {};
        form.setFieldsValue({
            ...record,
            i18n: parseI18n(record.i18n),
            cpu_time: record.cpu_time ? parseInt(record.cpu_time) / 1000000 : 900,
            ...price,
            ipv6_prefix_len: Number(record.ipv6_prefix_len || 0),
            ipv6_count: Number(record.ipv6_count || 1),
            // NAT plan = NAT address, no public IPv4 (shared node IPv4 + forwards / websites)
            network_type: Number(record.nat_ip_num || 0) > 0 && !Number(String(record.public_ip_num || 0).split('-')[0]) ? 'nat' : 'public',
            port_forward_num: Number(record.port_forward_num || 0) || 20,
            site_forward_num: Number(record.site_forward_num || 0) || 10,
            ...Object.fromEntries(Object.entries(QUOTA_DEFAULTS).map(([k, v]) => [k, record[k] ?? v])),
            // NULL on plans from before overage actions = shutdown (old behaviour).
            traffic_over_action: record.traffic_over_action || 'shutdown',
            // Ensure device config fields have values
            sys_disk_driver: record.sys_disk_driver || defaults.sys_disk_driver || 'virtio',
            sys_disk_cache: record.sys_disk_cache || defaults.sys_disk_cache || 'none',
            sys_disk_io: record.sys_disk_io || defaults.sys_disk_io || 'native',
            data_disk_driver: record.data_disk_driver || defaults.data_disk_driver || 'virtio',
            data_disk_cache: record.data_disk_cache || defaults.data_disk_cache || 'none',
            data_disk_io: record.data_disk_io || defaults.data_disk_io || 'native',
            net_driver: record.net_driver || defaults.net_driver || 'virtio',
            display_type: record.display_type || defaults.display_type || 'vnc',
            video_model: record.video_model || defaults.video_model || 'cirrus',
            vnc_keymap: record.vnc_keymap || defaults.vnc_keymap || 'en-us',
            cpu_mode: record.cpu_mode || defaults.cpu_mode || 'host-passthrough',
            machine_type: record.machine_type || defaults.machine_type || 'pc-q35-6.2',
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await evmAdminService.deleteTemplate(id);
            if (response.status === 'success') {
                message.success('Template deleted successfully');
                fetchTemplates();
            } else {
                message.error(response.msg || 'Failed to delete template');
            }
        } catch (error) {
            message.error('Failed to delete template');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            // Build price object
            const priceFields = ['month', 'quarter', 'half-year', 'year', 'two-year', 'triennium'];
            const price = {};
            priceFields.forEach(field => {
                if (values[field]) price[field] = values[field];
            });

            const submitData = {
                ...values,
                i18n: cleanI18n(values.i18n),
                cpu_time: values.cpu_time || 900, // seconds; the API stores it in µs
                price: JSON.stringify(price),
            };

            // Remove price fields from submitData
            priceFields.forEach(field => delete submitData[field]);

            const response = editingTemplate
                ? await evmAdminService.updateTemplate(editingTemplate.id, submitData)
                : await evmAdminService.createTemplate(submitData);

            if (response.status === 'success') {
                message.success(editingTemplate ? 'Template updated' : 'Template created');
                setModalVisible(false);
                fetchTemplates();
            } else {
                message.error(response.msg || 'Operation failed');
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
        },
        {
            title: 'Template Name',
            dataIndex: 'name',
            render: (text, record) => (
                <Space direction="vertical" size={0}>
                    <span style={{ fontWeight: 500 }}>{text}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.description?.substring(0, 50)}</Text>
                </Space>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            width: 80,
            render: (type) => (
                <Tag color={type === 'elastic' ? 'blue' : 'green'}>
                    {type === 'elastic' ? 'Elastic' : 'Static'}
                </Tag>
            ),
        },
        {
            title: 'CPU',
            dataIndex: 'core',
            width: 80,
            render: (core, record) => record.type === 'elastic' ? core : `${core} Core`,
        },
        {
            title: 'Memory',
            dataIndex: 'memory',
            width: 100,
            render: (mem, record) => record.type === 'elastic' ? mem : `${mem} MB`,
        },
        {
            title: 'System Disk',
            dataIndex: 'sys_disk_size',
            width: 100,
            render: (disk, record) => record.type === 'elastic' ? disk : `${disk} GB`,
        },
        {
            title: 'Bandwidth',
            width: 100,
            render: (_, record) => (
                <span>{record.net_in}/{record.net_out} Mbps</span>
            ),
        },
        {
            title: 'IPs',
            width: 110,
            render: (_, record) => (
                <Tooltip title={`Public: ${record.public_ip_num}, Private: ${record.private_ip_num}, NAT: ${record.nat_ip_num}`}>
                    <span>{record.public_ip_num || 0}{record.ipv6_prefix_len > 0 ? ` · v6 /${record.ipv6_prefix_len}${record.ipv6_count > 1 ? `×${record.ipv6_count}` : ''}` : ''}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 80,
            render: (status) => (
                <Tag color={status === 1 || status === '1' ? 'success' : 'default'}>
                    {status === 1 || status === '1' ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Delete this template?"
                        description="This action cannot be undone."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const renderConfigFields = () => {
        const isElastic = templateType === 'elastic';
        const placeholder = isElastic ? 'min-max-step (e.g., 1-8-1)' : '';

        return (
            <>
                <Divider>Resource Configuration {isElastic && <Tag color="blue">Elastic Mode</Tag>}</Divider>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="core"
                            label={<span>CPU Cores <Tooltip title={isElastic ? 'Format: min-max-step' : 'Fixed value'}><InfoCircleOutlined /></Tooltip></span>}
                            rules={[{ required: true }]}
                        >
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={1} max={128} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="memory"
                            label="Memory (MB)"
                            rules={[{ required: true }]}
                        >
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={64} max={512000} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="quota"
                            label={<span>CPU Quota (%) <Tooltip title="每 vCPU"><InfoCircleOutlined /></Tooltip></span>}
                            rules={[{ required: true }]}
                        >
                            <InputNumber min={1} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="cpu_time" label="CPU Time (sec/month)">
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="over_cpu" label="Over CPU Quota (%)">
                            <InputNumber min={0} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="flow_limit" label="Traffic Limit (GB, 0=unlimited)">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                </Row>

                <Divider>Storage</Divider>

                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="sys_disk_size" label="System Disk (GB)" rules={[{ required: true }]}>
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={20} max={2000} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="data_disk_size" label="Data Disk (GB)">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} max={2000} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="sys_disk_io_read" label="Sys IO Read (MB/s)">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="sys_disk_io_write" label="Sys IO Write (MB/s)">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="sys_disk_iops_read" label="Sys Read IOPS" tooltip="0 = unlimited">
                            <InputNumber min={0} max={1000000} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="sys_disk_iops_write" label="Sys Write IOPS" tooltip="0 = unlimited">
                            <InputNumber min={0} max={1000000} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="data_disk_iops_read" label="Data Read IOPS" tooltip="0 = unlimited">
                            <InputNumber min={0} max={1000000} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="data_disk_iops_write" label="Data Write IOPS" tooltip="0 = unlimited">
                            <InputNumber min={0} max={1000000} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider>Network</Divider>

                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="net_in" label="Inbound (Mbps)" rules={[{ required: true }]}>
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} max={10000} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="net_out" label="Outbound (Mbps)" rules={[{ required: true }]}>
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} max={10000} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="private_net_in" label="Private In (Mbps)">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="private_net_out" label="Private Out (Mbps)">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="network_type" label={<span>网络类型 <Tooltip title="NAT：共享节点公网 IPv4，用户通过端口转发和网站（域名）转发访问；无独立公网 IPv4"><InfoCircleOutlined /></Tooltip></span>}>
                            <Select options={[{ value: 'public', label: '公网 IPv4' }, { value: 'nat', label: 'NAT（共享 IPv4）' }]} />
                        </Form.Item>
                    </Col>
                    <Form.Item noStyle shouldUpdate={(a, b) => a.network_type !== b.network_type}>
                        {({ getFieldValue }) => (getFieldValue('network_type') === 'nat' ? (
                            <>
                                <Col span={8}>
                                    <Form.Item name="port_forward_num" label="转发数" tooltip="每台实例的 TCP/UDP 端口转发规则上限">
                                        <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="site_forward_num" label="建站数" tooltip="每台实例的网站（域名，80/443 共享）上限">
                                        <InputNumber min={0} max={1000} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </>
                        ) : null)}
                    </Form.Item>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="public_ip_num" label="Public IPs">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} max={10} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="private_ip_num" label="Private IPs">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} max={10} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="nat_ip_num" label="NAT IPs">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} max={10} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="ipv6_prefix_len" label={<span>IPv6 <Tooltip title="套餐内含的 IPv6 块，从节点 IPv6 池分配；节点无可用块时不会选中该节点"><InfoCircleOutlined /></Tooltip></span>}>
                            <Select options={[{ value: 0, label: '无' }, ...[64, 60, 56, 52, 48].map((l) => ({ value: l, label: `/${l}` }))]} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item noStyle shouldUpdate={(a, b) => a.ipv6_prefix_len !== b.ipv6_prefix_len}>
                            {({ getFieldValue }) => (getFieldValue('ipv6_prefix_len') > 0 ? (
                                <Form.Item name="ipv6_count" label="IPv6 块数">
                                    <InputNumber min={1} max={16} style={{ width: '100%' }} />
                                </Form.Item>
                            ) : null)}
                        </Form.Item>
                    </Col>
                </Row>

                <Divider>超额限制</Divider>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="cpu_quota_core_hours" label={<span>CPU 核时/月 <Tooltip title="内部配额，用户不可见；0 为不限"><InfoCircleOutlined /></Tooltip></span>}>
                            <InputNumber min={0} step={10} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="io_quota_gb" label={<span>磁盘读写 GB/月 <Tooltip title="读+写合计，内部配额；0 为不限"><InfoCircleOutlined /></Tooltip></span>}>
                            <InputNumber min={0} step={100} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="io_read_quota_gb" label="其中读 GB">
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="io_write_quota_gb" label="其中写 GB">
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    {[['traffic_over_action', '流量超额'], ['cpu_over_action', 'CPU 超额'], ['io_over_action', 'IO 超额']].map(([name, label]) => (
                        <Col span={8} key={name}>
                            <Form.Item name={name} label={label}>
                                <Select options={name === 'traffic_over_action' ? TRAFFIC_OVER_ACTIONS : OVER_ACTIONS} />
                            </Form.Item>
                        </Col>
                    ))}
                </Row>

                <Row gutter={16}>
                    <Col span={4}>
                        <Form.Item name="throttle_net_mbps" label="限速 Mbps">
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="throttle_cpu_pct" label={<span>CPU % <Tooltip title="每 vCPU"><InfoCircleOutlined /></Tooltip></span>}>
                            <InputNumber min={1} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="throttle_io_read_mbps" label="读 MB/s">
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="throttle_io_write_mbps" label="写 MB/s">
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="throttle_io_read_iops" label="读 IOPS">
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="throttle_io_write_iops" label="写 IOPS">
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider>Backup & Snapshots</Divider>

                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="snapshot_num" label="Max Snapshots">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} max={30} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="backup_num" label="Max Backups">
                            {isElastic ? <Input placeholder={placeholder} /> : <InputNumber min={0} max={30} style={{ width: '100%' }} />}
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="image_id" label="Default Image">
                            <Select placeholder="Select image" allowClear>
                                {images.map(img => (
                                    <Option key={img.id} value={img.id}>{img.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                            <Select>
                                <Option value={1}>Active</Option>
                                <Option value={0}>Inactive</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="backup_points" label={t('Auto backup restore points')} tooltip={t('0 = not included')}>
                            <InputNumber min={0} max={365} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="backup_interval_hours" label={t('Interval')}>
                            <InputNumber min={1} max={744} addonAfter={t('hours')} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider>Pricing</Divider>

                <Row gutter={16}>
                    <Col span={4}>
                        <Form.Item name="month" label="Monthly">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="quarter" label="Quarterly">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="half-year" label="Half Year">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="year" label="Yearly">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="two-year" label="2 Years">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="triennium" label="3 Years">
                            <InputNumber min={0} style={{ width: '100%' }} prefix={currencySymbol(PLATFORM_CURRENCY)} />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider>
                    <Space>
                        <SettingOutlined />
                        Device Configuration
                        <Switch
                            size="small"
                            checked={showDeviceConfig}
                            onChange={setShowDeviceConfig}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>Advanced</Text>
                    </Space>
                </Divider>

                {showDeviceConfig && deviceOptions && (
                    <Collapse defaultActiveKey={['disk', 'network', 'display', 'cpu']} ghost>
                        <Panel header={<><HddOutlined /> Disk Configuration</>} key="disk">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="sys_disk_driver" label="System Disk Driver">
                                        <Select>
                                            {deviceOptions.options?.disk_drivers?.map(d => (
                                                <Option key={d} value={d}>{d.toUpperCase()}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="sys_disk_cache" label="System Disk Cache">
                                        <Select>
                                            {deviceOptions.options?.disk_cache_modes?.map(c => (
                                                <Option key={c} value={c}>{c}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="sys_disk_io" label="System Disk IO">
                                        <Select>
                                            {deviceOptions.options?.disk_io_modes?.map(io => (
                                                <Option key={io} value={io}>{io}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="data_disk_driver" label="Data Disk Driver">
                                        <Select>
                                            {deviceOptions.options?.disk_drivers?.map(d => (
                                                <Option key={d} value={d}>{d.toUpperCase()}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="data_disk_cache" label="Data Disk Cache">
                                        <Select>
                                            {deviceOptions.options?.disk_cache_modes?.map(c => (
                                                <Option key={c} value={c}>{c}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="data_disk_io" label="Data Disk IO">
                                        <Select>
                                            {deviceOptions.options?.disk_io_modes?.map(io => (
                                                <Option key={io} value={io}>{io}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>

                        <Panel header={<><ApiOutlined /> Network Configuration</>} key="network">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="net_driver" label="Network Driver">
                                        <Select>
                                            {deviceOptions.options?.net_drivers?.map(d => (
                                                <Option key={d} value={d}>{d}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>

                        <Panel header={<><DesktopOutlined /> Display Configuration</>} key="display">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="display_type" label="Display Type">
                                        <Select>
                                            {deviceOptions.options?.display_types?.map(d => (
                                                <Option key={d} value={d}>{d.toUpperCase()}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="video_model" label="Video Model">
                                        <Select>
                                            {deviceOptions.options?.video_models?.map(v => (
                                                <Option key={v} value={v}>{v}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="vnc_keymap" label="VNC Keymap">
                                        <Select>
                                            {deviceOptions.options?.vnc_keymaps?.map(k => (
                                                <Option key={k} value={k}>{k}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>

                        <Panel header={<><SettingOutlined /> CPU & Machine</>} key="cpu">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="cpu_mode" label="CPU Mode">
                                        <Select>
                                            {deviceOptions.options?.cpu_modes?.map(m => (
                                                <Option key={m} value={m}>{m}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="machine_type" label="Machine Type">
                                        <Input placeholder="e.g., pc-q35-6.2" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                )}
            </>
        );
    };

    return (
        <div>
            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>模板管理</Title>
                    <Space>
                        <Input
                            placeholder="Search..."
                            prefix={<SearchOutlined />}
                            value={filters.keyword}
                            onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                            style={{ width: 200 }}
                            allowClear
                        />
                        <Select
                            placeholder="Type"
                            value={filters.type || undefined}
                            onChange={(value) => setFilters({ ...filters, type: value })}
                            style={{ width: 120 }}
                            allowClear
                        >
                            <Option value="static">Static</Option>
                            <Option value="elastic">Elastic</Option>
                        </Select>
                        <Button icon={<ReloadOutlined />} onClick={fetchTemplates}>Refresh</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreate('static')}>
                            Add Static
                        </Button>
                        <Button icon={<PlusOutlined />} onClick={() => handleCreate('elastic')}>
                            Add Elastic
                        </Button>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={templates}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} templates`,
                        onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
                    }}
                />
            </Card>

            <Modal
                title={editingTemplate ? 'Edit Template' : `Create ${templateType === 'elastic' ? 'Elastic' : 'Static'} Template`}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                width={1000}
                style={{ top: 20 }}
            >
                <Form form={form} layout="vertical">
                    <I18nFieldTabs
                        fields={[{ key: 'name', label: 'Template Name' }, { key: 'description', label: 'Description', type: 'textarea' }]}
                        base={<>
                            <Form.Item
                                name="name"
                                label="Template Name"
                                rules={[{ required: true, message: 'Please enter template name' }]}
                            >
                                <Input placeholder="e.g., Basic Plan 1C1G" />
                            </Form.Item>
                            <Form.Item
                                name="description"
                                label="Description"
                                rules={[{ required: true, message: 'Please enter description' }]}
                            >
                                <TextArea rows={2} placeholder="Template description..." />
                            </Form.Item>
                        </>}
                    />
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="type"
                                label="Template Type"
                                rules={[{ required: true }]}
                            >
                                <Select onChange={(val) => setTemplateType(val)}>
                                    <Option value="static">Static (Fixed values)</Option>
                                    <Option value="elastic">Elastic (Range values)</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {renderConfigFields()}
                </Form>
            </Modal>
        </div>
    );
};

export default TemplateManagement;
